import { AddUniversal } from '@duxton-web/icons';
import {
  Button, Card, Empty, Flex, Input, Modal, Tag, Tooltip, Typography,
} from '@duxton-web/ui';
import { targetUserTypes } from 'data/trident-types';
import i18n from 'i18n-js';
import SegPWidget from 'omega-rtc/components/form/ucd/common/SegPWidget';
import type { FunnelObjective } from 'omega-rtc/types/ts/content-config';
import React, { useEffect, useRef, useState } from 'react';

import { ensureFunnelObjectiveLocalIDs, isFunnelObjectiveComplete } from './adapters';
import { contentConfigModalStyle } from './theme';

const { TextArea } = Input;
const OBJECTIVE_LOCALE = 'ucd_content_config.product_offerings.objectives';
const objectiveText = (key, options = {}) => i18n.t(`${OBJECTIVE_LOCALE}.${key}`, options);

const EMPTY_OBJECTIVE: FunnelObjective = {
  id: '', name: '', description: '', source: '',
};

// Staged editor for a Product's funnel objectives, mirroring the Audience
// configuration modal: edits live in a local draft and only reach the page's
// edit sandbox on Apply. Apply is blocked while any objective is missing a
// name, description, or source (the backend rejects blank values). `id` is
// UCD-owned: '' for a new objective (UCD mints the UUID), preserved unchanged
// for existing ones; apply stamps a browser-side `localID` on the id-less ones.
export const FunnelObjectivesModal = ({
  disabled = false,
  onApply,
  onClose,
  open,
  value = [],
}: {
  disabled?: boolean;
  onApply: (next: FunnelObjective[]) => void;
  onClose: () => void;
  open: boolean;
  value: FunnelObjective[];
}) => {
  const [draftObjectives, setDraftObjectives] = useState<FunnelObjective[]>([]);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraftObjectives(value.map((objective) => ({ ...objective })));
    }
    wasOpen.current = open;
  }, [open, value]);

  const hasIncomplete = draftObjectives.some(
    (objective) => !isFunnelObjectiveComplete(objective),
  );
  // Functional updates: the segment widget reports selections through window
  // "message" events, so updates must not depend on a render-time closure.
  const setField = (index, field, fieldValue) => setDraftObjectives(
    (prev) => prev.map((objective, i) => (
      i === index ? { ...objective, [field]: fieldValue } : objective
    )),
  );
  const addObjective = () => setDraftObjectives((prev) => [...prev, { ...EMPTY_OBJECTIVE }]);
  const removeObjective = (index) => setDraftObjectives(
    (prev) => prev.filter((_, i) => i !== index),
  );
  const apply = () => {
    if (hasIncomplete) return;
    // Stamp a local id on anything UCD has no id for, so a generation run can
    // name the objective before it is ever saved.
    onApply(ensureFunnelObjectiveLocalIDs(draftObjectives));
  };

  return (
    <Modal
      title={objectiveText('details')}
      open={open}
      onCancel={onClose}
      width={1200}
      style={contentConfigModalStyle}
      className="cc-modal cc-objective-modal"
      footer={(
        // The CDP segment widget overlays an absolutely-positioned iframe at
        // z-index 1010 while its dropdown is open; keep the actions above it
        // so Cancel/Apply always stay clickable.
        <div className="cc-objective-modal-footer">
          <Flex justify="flex-end" gap={8}>
            <Button onClick={onClose}>
              {disabled ? objectiveText('close') : objectiveText('cancel')}
            </Button>
            {!disabled && (
              <Tooltip title={hasIncomplete ? objectiveText('incomplete') : ''}>
                <Button type="primary" disabled={hasIncomplete} onClick={apply}>
                  {objectiveText('apply')}
                </Button>
              </Tooltip>
            )}
          </Flex>
        </div>
      )}
    >
      <Flex vertical gap="middle">
        {!disabled && (
          <div>
            <Button size="small" icon={<AddUniversal />} onClick={addObjective}>
              {objectiveText('add')}
            </Button>
          </div>
        )}
        {draftObjectives.length === 0 && (
          <Empty description={objectiveText('no_selection')} />
        )}
        {draftObjectives.length > 0 && (
          <div className="cc-objective-grid">
            {draftObjectives.map((objective, index) => (
              <div className="cc-objective-item" key={objective.id || `new-${index}`}>
                <Flex vertical gap="small">
                  <Flex justify="space-between" align="center" gap={8}>
                    <Typography.Body level={2} strong>
                      {objective.name.trim() || objectiveText('new_objective')}
                    </Typography.Body>
                    {!disabled && (
                      <Button size="small" onClick={() => removeObjective(index)}>
                        {objectiveText('remove')}
                      </Button>
                    )}
                  </Flex>
                  <div>
                    <Typography.Body level={3} strong className="cc-objective-field-label">
                      {objectiveText('name_label')}
                    </Typography.Body>
                    <Input
                      aria-label={`${objectiveText('name_label')} ${index + 1}`}
                      value={objective.name}
                      onChange={(e) => setField(index, 'name', e.target.value)}
                      placeholder={objectiveText('name_placeholder')}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <Typography.Body level={3} strong className="cc-objective-field-label">
                      {objectiveText('description_label')}
                    </Typography.Body>
                    <TextArea
                      aria-label={`${objectiveText('description_label')} ${index + 1}`}
                      value={objective.description}
                      onChange={(e) => setField(index, 'description', e.target.value)}
                      placeholder={objectiveText('description_placeholder')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <Typography.Body level={3} strong className="cc-objective-field-label">
                      {objectiveText('source_label')}
                    </Typography.Body>
                    {disabled ? (
                      <Input
                        aria-label={`${objectiveText('source_label')} ${index + 1}`}
                        value={objective.source}
                        disabled
                      />
                    ) : (
                      <SegPWidget
                        targetUserType={targetUserTypes.pax}
                        onChange={(value?: string) => setField(index, 'source', value || '')}
                        onSelect={() => {}}
                        segmentVal={objective.source}
                        maxVisible={5}
                        enabledSegmentNSFormat
                        showNsDropdown
                      />
                    )}
                  </div>
                </Flex>
              </div>
            ))}
          </div>
        )}
      </Flex>
    </Modal>
  );
};

// Funnel Objectives summary card for the Product Offering detail, following
// the Audience card flow: title + count on the left, a details button on the
// right, and name chips (description · source in the tooltip) below. All
// management happens in the modal.
export const FunnelObjectivesCard = ({
  disabled,
  objectives = [],
  onChange,
}: {
  disabled: boolean;
  objectives: FunnelObjective[];
  onChange: (next: FunnelObjective[]) => void;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <Card bordered={false}>
      <Flex vertical gap="small">
        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Flex align="center" gap={8}>
            <Typography.Body level={2} strong className="cc-title-reset">
              {objectiveText('title')}
            </Typography.Body>
            <Tag>{objectives.length}</Tag>
          </Flex>
          <Button size="small" onClick={() => setModalOpen(true)}>
            {objectiveText('details')}
          </Button>
        </Flex>
        {objectives.length === 0 ? (
          <Typography.Body level={2} type="secondary">
            {objectiveText('not_configured')}
          </Typography.Body>
        ) : (
          <Flex gap={6} wrap="wrap">
            {objectives.map((objective, index) => (
              <Tooltip
                title={`${objective.description} · ${objective.source}`}
                key={objective.id || `new-${index}`}
              >
                <Tag color="cyan" className="cc-tag-gap">{objective.name}</Tag>
              </Tooltip>
            ))}
          </Flex>
        )}
      </Flex>
      <FunnelObjectivesModal
        disabled={disabled}
        onApply={(next) => {
          onChange(next);
          setModalOpen(false);
        }}
        onClose={() => setModalOpen(false)}
        open={modalOpen}
        value={objectives}
      />
    </Card>
  );
};

export default FunnelObjectivesCard;
